import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus,
  Lock,
  Mail,
  Sunrise,
  Trash2,
  ChevronDown,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import {
  listLetters,
  addLetter,
  markRead,
  deleteLetter,
  presetArrival,
} from "@/lib/lettersVault";
import { scheduleLetterArrival, cancelLetterArrival } from "@/lib/native";
import { useAutoSavedDraft } from "@/lib/useAutoSavedDraft";
import AutoSaveIndicator from "@/components/AutoSaveIndicator";
import { LETTERS } from "@/constants/testIds";

const PRESETS = [
  { id: "tomorrow", label: "tomorrow morning" },
  { id: "in-3-days", label: "in 3 days" },
  { id: "next-week", label: "next week" },
  { id: "in-a-month", label: "in a month" },
];

function formatArrival(iso) {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, tomorrow)) return "tomorrow morning";
  if (sameDay(d, today)) return "this morning";
  const opts = { month: "short", day: "numeric" };
  return d.toLocaleDateString(undefined, opts);
}

export default function Letters() {
  const [letters, setLetters] = useState([]);
  const [composing, setComposing] = useState(false);
  const {
    value: draft,
    setValue: setDraft,
    clear: clearDraft,
  } = useAutoSavedDraft("letters", { text: "", preset: "tomorrow" });
  const text = draft.text;
  const preset = draft.preset;
  const setText = (next) =>
    setDraft((d) => ({ ...d, text: typeof next === "function" ? next(d.text) : next }));
  const setPreset = (next) => setDraft((d) => ({ ...d, preset: next }));
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listLetters().then((next) => {
      if (!cancelled) setLetters(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // If a draft was restored from the vault, re-open the composer so the
  // user sees it. Mount-only check.
  useEffect(() => {
    if (draft.text && draft.text.trim().length > 0) {
      setComposing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const sealed = letters.filter((l) => l.derivedStatus === "sealed");
    const arrived = letters.filter(
      (l) => l.derivedStatus === "arrived" || l.derivedStatus === "read" || l.derivedStatus === "broken",
    );
    // arrived sorted newest arrival first
    arrived.sort(
      (a, b) => new Date(b.arrivesAt).getTime() - new Date(a.arrivesAt).getTime(),
    );
    sealed.sort(
      (a, b) => new Date(a.arrivesAt).getTime() - new Date(b.arrivesAt).getTime(),
    );
    return { sealed, arrived };
  }, [letters]);

  const handleSend = async () => {
    if (!text.trim()) return;
    try {
      const entry = await addLetter({
        text,
        arrivesAt: presetArrival(preset).toISOString(),
      });
      setLetters((arr) => [...arr, { ...entry, derivedStatus: "sealed" }]);
      // Native: schedule a quiet OS notification at arrival (no-op on web).
      scheduleLetterArrival(entry).catch(() => undefined);
      clearDraft();
      setComposing(false);
      toast("Sent forward.", {
        description: `Tomorrow-you will find this ${formatArrival(entry.arrivesAt)}.`,
      });
    } catch (_e) {
      toast("Couldn't seal that just now.", {
        description: "Try again in a moment — nothing was lost.",
      });
    }
  };

  const handleOpen = async (letter) => {
    if (letter.derivedStatus === "sealed") return; // sealed letters: separate handler
    setOpenId((id) => (id === letter.id ? null : letter.id));
    if (letter.status !== "read" && letter.status !== "broken") {
      const updated = await markRead(letter);
      setLetters((arr) =>
        arr.map((l) =>
          l.id === letter.id
            ? { ...updated, derivedStatus: updated.status === "broken" ? "broken" : "read" }
            : l,
        ),
      );
    }
  };

  const handleBreakSeal = async (letter) => {
    const ok = window.confirm(
      "Open this letter early?\n\nYou wrote it for a later you. It's okay to reach for it now — we'll just note that you did.",
    );
    if (!ok) return;
    const updated = await markRead(letter);
    setLetters((arr) =>
      arr.map((l) =>
        l.id === letter.id ? { ...updated, derivedStatus: "broken" } : l,
      ),
    );
    setOpenId(letter.id);
  };

  const handleDelete = async (id) => {
    await deleteLetter(id);
    cancelLetterArrival(id).catch(() => undefined);
    setLetters((arr) => arr.filter((l) => l.id !== id));
    toast("Letter released.");
  };

  return (
    <PageContainer testid={LETTERS.root}>
      <PageHeader
        title="Tomorrow-you"
        subtitle="Letters you've sent forward in time."
        variant="exit"
        exitTo="/home"
        exitLabel="Exit"
        testid={LETTERS.exit}
      />

      {/* Privacy strip */}
      <div
        className="rounded-2xl p-3 mb-5 flex items-center gap-2.5"
        style={{ background: "rgba(74,32,64,0.05)" }}
      >
        <Lock size={14} style={{ color: "var(--un-plum)" }} />
        <p
          className="text-[12px] leading-snug"
          style={{ color: "var(--un-text-2)" }}
        >
          Sealed and stored <strong>only on your device</strong>. On supported devices, a quiet arrival reminder may appear if notifications are allowed; it never includes your letter text.
        </p>
      </div>

      {/* Compose CTA */}
      {!composing && (
        <SoftButton
          data-testid={LETTERS.newBtn}
          variant="primary"
          size="lg"
          iconLeft={<Plus size={18} />}
          onClick={() => setComposing(true)}
          className="w-full mb-6"
        >
          Write tomorrow-you a letter
        </SoftButton>
      )}

      <AnimatePresence>
        {composing && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="rounded-3xl p-4 mb-6"
            style={{
              background: "var(--un-bg-2)",
              boxShadow: "0 8px 28px rgba(74,32,64,0.08)",
            }}
          >
            <p
              className="italic text-[15px] mb-3"
              style={{
                color: "var(--un-text-3)",
                fontFamily: "Cormorant Garamond, serif",
              }}
            >
              Tell tomorrow-you what feels true right now. They can wait.
            </p>
            <textarea
              data-testid={LETTERS.textarea}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder="Dear tomorrow-you…"
              className="w-full p-3 rounded-2xl text-[15px] leading-relaxed resize-none focus:outline-none"
              style={{
                background: "#FDFBF7",
                border: "1px solid rgba(74,32,64,0.10)",
                color: "var(--un-text-1)",
                fontFamily: "DM Sans, sans-serif",
              }}
            />

            <div className="mt-1.5 flex justify-end">
              <AutoSaveIndicator watch={text} />
            </div>

            <p
              className="text-[11px] uppercase tracking-[0.2em] mt-4 mb-2"
              style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
            >
              Arrives
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {PRESETS.map((p) => {
                const active = preset === p.id;
                return (
                  <button
                    key={p.id}
                    data-testid={`${LETTERS.presetPrefix}${p.id}`}
                    onClick={() => setPreset(p.id)}
                    className="px-3.5 py-1.5 rounded-full text-xs font-medium un-press transition-all"
                    style={{
                      background: active ? "var(--un-plum)" : "rgba(74,32,64,0.05)",
                      color: active ? "#FDFBF7" : "var(--un-text-2)",
                      fontFamily: "Manrope, sans-serif",
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-2">
              <SoftButton
                data-testid={LETTERS.cancel}
                variant="ghost"
                size="md"
                onClick={() => {
                  setComposing(false);
                  // Buffer is preserved — when the user returns, the draft is restored.
                }}
                className="flex-1"
              >
                Not now
              </SoftButton>
              <SoftButton
                data-testid={LETTERS.send}
                variant="primary"
                size="md"
                iconRight={<Send size={16} />}
                onClick={handleSend}
                disabled={!text.trim()}
                className="flex-1"
              >
                Send forward
              </SoftButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sealed letters */}
      {grouped.sealed.length > 0 && (
        <section className="mb-7">
          <p
            className="text-[11px] uppercase tracking-[0.22em] mb-3"
            style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
          >
            Sealed
          </p>
          <div className="flex flex-col gap-3">
            {grouped.sealed.map((l) => (
              <SealedCard
                key={l.id}
                letter={l}
                onBreak={() => handleBreakSeal(l)}
                onDelete={() => handleDelete(l.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Arrived letters */}
      {grouped.arrived.length > 0 && (
        <section className="mb-10">
          <p
            className="text-[11px] uppercase tracking-[0.22em] mb-3"
            style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
          >
            From you
          </p>
          <div className="flex flex-col gap-3">
            {grouped.arrived.map((l) => (
              <ArrivedCard
                key={l.id}
                letter={l}
                isOpen={openId === l.id}
                onOpen={() => handleOpen(l)}
                onDelete={() => handleDelete(l.id)}
              />
            ))}
          </div>
        </section>
      )}

      {letters.length === 0 && !composing && <EmptyLetters />}
    </PageContainer>
  );
}

function SealedCard({ letter, onBreak, onDelete }) {
  return (
    <motion.div
      data-testid={`${LETTERS.sealedPrefix}${letter.id}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl p-4 relative"
      style={{
        background: "rgba(74,32,64,0.04)",
        border: "1px dashed rgba(74,32,64,0.18)",
      }}
    >
      <div className="flex items-start gap-3">
        <Mail size={16} style={{ color: "var(--un-plum)" }} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p
            className="text-[14px] font-medium"
            style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}
          >
            Arrives {formatArrival(letter.arrivesAt)}
          </p>
          <p
            className="italic text-[15px] mt-0.5"
            style={{
              color: "var(--un-text-3)",
              fontFamily: "Cormorant Garamond, serif",
            }}
          >
            A letter from a past you is waiting.
          </p>
          <div className="flex gap-3 mt-3">
            <button
              data-testid={`${LETTERS.breakPrefix}${letter.id}`}
              onClick={onBreak}
              className="text-[12px] underline underline-offset-2"
              style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
            >
              open early
            </button>
            <button
              data-testid={`${LETTERS.deletePrefix}${letter.id}`}
              onClick={onDelete}
              className="text-[12px] underline underline-offset-2"
              style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
            >
              release
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ArrivedCard({ letter, isOpen, onOpen, onDelete }) {
  const isBroken = letter.derivedStatus === "broken" || letter.status === "broken";
  return (
    <motion.div
      data-testid={`${LETTERS.arrivedPrefix}${letter.id}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl p-4"
      style={{
        background:
          "linear-gradient(135deg, #F4E6C8 0%, #E8D2A8 60%, #D6B884 100%)",
        boxShadow: "0 10px 26px rgba(196,161,106,0.22), 0 2px 8px rgba(74,45,72,0.10)",
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left un-press flex items-start gap-3"
      >
        <Sunrise size={16} style={{ color: "#8A5A1F" }} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p
            className="text-[14px] font-medium"
            style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
          >
            {isBroken ? "Opened early" : `Arrived ${formatArrival(letter.arrivesAt)}`}
          </p>
          <p
            className="italic text-[15px] mt-0.5"
            style={{
              color: "var(--un-text-3)",
              fontFamily: "Cormorant Garamond, serif",
            }}
          >
            {isOpen ? "" : "Tap to read what you wrote."}
          </p>
        </div>
        <ChevronDown
          size={16}
          style={{
            color: "var(--un-text-3)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.3s",
          }}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid={`${LETTERS.bodyPrefix}${letter.id}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <p
              className="mt-4 text-[15px] leading-relaxed whitespace-pre-wrap"
              style={{
                color: "var(--un-text-1)",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {letter.text}
            </p>
            <button
              type="button"
              data-testid={`${LETTERS.deletePrefix}${letter.id}`}
              onClick={onDelete}
              className="mt-4 inline-flex items-center gap-1.5 text-[12px] un-press"
              style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
            >
              <Trash2 size={12} /> release this letter
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function EmptyLetters() {
  return (
    <motion.div
      data-testid={LETTERS.emptyState}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="text-center pt-2 pb-12 px-2"
    >
      <div
        className="mx-auto mb-5 rounded-3xl"
        style={{
          width: 96,
          height: 64,
          background:
            "linear-gradient(135deg, #F4E6C8 0%, #E0C896 100%)",
          boxShadow: "0 8px 22px rgba(196,161,106,0.18)",
          transform: "rotate(-3deg)",
        }}
      />
      <p
        className="text-[17px] font-medium mb-1.5"
        style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
      >
        Nothing in the post yet
      </p>
      <p
        className="italic text-[17px] leading-relaxed max-w-[300px] mx-auto"
        style={{
          color: "var(--un-text-3)",
          fontFamily: "Cormorant Garamond, serif",
        }}
      >
        Tonight's thoughts are not always tomorrow's truth. Write one forward
        — see how it feels in the morning.
      </p>
    </motion.div>
  );
}
